/**
 * Shared display / input formatters for Advisor sessions and payouts.
 *
 * These were duplicated across SessionsList, SessionDetail, SessionBook,
 * SessionRoom and Payouts. Keep one copy so money and timestamps render
 * identically everywhere in the Sessions surface.
 */

const EM_DASH = '—'

const pad2 = (n: number) => String(n).padStart(2, '0')

/** Half-hour clock values covering a full local day: 00:00–23:30. */
export const SESSION_HALF_HOUR_SLOTS: Array<{ value: string; label: string }> = []
for (let hour = 0; hour < 24; hour += 1) {
  for (const minute of [0, 30]) {
    SESSION_HALF_HOUR_SLOTS.push({
      value: `${pad2(hour)}:${pad2(minute)}`,
      label: new Date(2000, 0, 1, hour, minute).toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      }),
    })
  }
}

/** Preset chips — startups3 still accepts any integer in SESSION_MIN/MAX_MINUTES. */
export const SESSION_PACKAGE_MINUTES = [15, 30, 45, 60] as const
export const SESSION_DEFAULT_PACKAGE_MINUTES = 30
export const SESSION_MIN_MINUTES = 1
export const SESSION_MAX_MINUTES = 180

export type SessionPackageMinutes = (typeof SESSION_PACKAGE_MINUTES)[number]

export const isSessionDurationMinutes = (value: number): boolean =>
  Number.isInteger(value) && value >= SESSION_MIN_MINUTES && value <= SESSION_MAX_MINUTES

export const estimateSessionCostUsd = (hourlyRate: number, minutes: number): number => {
  const rate = Number(hourlyRate) || 0
  const mins = Number(minutes) || SESSION_DEFAULT_PACKAGE_MINUTES
  return Math.round(((rate * mins) / 60) * 100) / 100
}

export const packageWindowEnded = (
  scheduledAt?: string | null,
  approximateLengthMinutes?: number | null
): boolean => {
  if (!scheduledAt || !approximateLengthMinutes) {
    return false
  }
  const start = new Date(scheduledAt).getTime()
  if (Number.isNaN(start)) {
    return false
  }
  return Date.now() > start + Number(approximateLengthMinutes) * 60 * 1000
}

/** `$1234.50`, or an em dash when the value is missing / not a number. */
export const formatUsd = (value?: number | null): string => {
  if (value == null || Number.isNaN(Number(value))) {
    return EM_DASH
  }
  return `$${Number(value).toFixed(2)}`
}

/** `$150/hr`, `Free`, or null when the rate is unset. */
export const formatHourlyRate = (hourlyRate?: number | null): string | null => {
  if (hourlyRate == null || Number.isNaN(Number(hourlyRate))) {
    return null
  }
  const rate = Number(hourlyRate)
  if (rate <= 0) {
    return 'Free'
  }
  return `$${rate.toFixed(0)}/hr`
}

/** Directory / profile / card CTA. Rate is shown next to the button, not in the label. */
export const bookSessionLabel = (): string => 'Book a Session'

/** The viewer's IANA timezone, falling back to Pacific when unavailable. */
export const browserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles'
  } catch {
    return 'America/Los_Angeles'
  }
}

/**
 * Short timezone label for scheduling copy, e.g. `PDT`.
 * Pass `timeZone` for an expert's zone; pass `at` so DST matches that instant.
 */
export const timezoneAbbreviation = (timeZone?: string | null, at?: Date): string => {
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      ...(timeZone ? { timeZone } : {}),
      timeZoneName: 'short',
    }).formatToParts(at ?? new Date())
    return parts.find(part => part.type === 'timeZoneName')?.value || ''
  } catch {
    return ''
  }
}

/** Locale date + time, or an em dash when missing / unparseable. */
export const formatWhen = (value?: string | null): string => {
  if (!value) {
    return EM_DASH
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return String(value)
  }
  const formatted = date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  const abbr = timezoneAbbreviation(undefined, date)
  return abbr && !formatted.includes(abbr) ? `${formatted} ${abbr}` : formatted
}

/** Split date / time for hero layouts. `null` when missing or unparseable. */
export const formatWhenParts = (value?: string | null): { date: string; time: string } | null => {
  if (!value) {
    return null
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  const clock = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  const abbr = timezoneAbbreviation(undefined, date)
  return {
    date: date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }),
    time: abbr ? `${clock} ${abbr}` : clock,
  }
}

const LOCAL_INPUT_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/

/**
 * `<input type="datetime-local">` value → ISO string for a GraphQL DateTime.
 * Parsed as the viewer's wall clock (not UTC) so a 10:00 pick stays 10:00 local.
 */
export const localToIso = (value: string): string | null => {
  if (!value) {
    return null
  }
  const match = LOCAL_INPUT_RE.exec(value)
  if (match) {
    const date = new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4]),
      Number(match[5])
    )
    if (Number.isNaN(date.getTime())) {
      return null
    }
    return date.toISOString()
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return date.toISOString()
}

/** ISO string → `<input type="datetime-local">` value in the viewer's timezone. */
export const isoToLocalInput = (value?: string | null): string => {
  if (!value) {
    return ''
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(
    date.getHours()
  )}:${pad2(date.getMinutes())}`
}

type NamedUser = { fullName?: string | null; displayName?: string | null } | null | undefined

/** Best available human name, with a caller-chosen fallback. */
export const personName = (user: NamedUser, fallback = EM_DASH): string =>
  user?.fullName || user?.displayName || fallback

/** Trigger a browser download from a text payload (ICS, etc.). */
export const downloadTextFile = (fileName: string, content: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

/**
 * ScoAdvisorSession.recording / transcript are JSON scalars: `{ url }` or a
 * stringified object. Return a playable/downloadable URL or null.
 */
export const sessionMediaUrl = (value: unknown): string | null => {
  if (!value) {
    return null
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) {
      return null
    }
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed
    }
    try {
      return sessionMediaUrl(JSON.parse(trimmed))
    } catch {
      return null
    }
  }
  if (typeof value === 'object' && value && 'url' in value) {
    const url = (value as { url?: unknown }).url
    return typeof url === 'string' && url ? url : null
  }
  return null
}

/** Strips Apollo's `GraphQL error:` prefix so users see the server message only. */
export const cleanErrorMessage = (error: unknown, fallback: string): string => {
  const message = error instanceof Error ? error.message : ''
  return (message || fallback).replace(/^GraphQL error:\s*/i, '')
}
